import React from 'react';
import { Callout, Figure } from './ArticleLayout';
import { ParamTable, StepList, BulletList } from './GuideLayout';
import { BACKEND_URL } from '../../lib/api';

// Static articles' original figures are frontend public assets ("/articles/bms/...",
// served by the frontend itself); admin-uploaded figures are backend-served
// ("/uploads/..."). Resolve each to a usable <img> src accordingly.
export const resolveImageSrc = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('/uploads/')) return `${BACKEND_URL}${imagePath}`;
  return imagePath;
};

// `!my-4` for the same reason as Figure's `!my-8` in ArticleLayout.jsx — the ancestor
// ArticleSection's `space-y-4` wrapper would otherwise override this block's own margin.
const ImageGallery = ({ images }) => (
  <div className="!my-4 grid grid-cols-2 md:grid-cols-3 gap-4">
    {(images || []).map((img, i) => (
      <figure key={i} className="m-0">
        <img
          src={resolveImageSrc(img.path)}
          alt={img.alt || img.caption || ''}
          className="block w-full h-40 object-cover rounded-sm border border-structure mx-auto"
        />
        {img.caption && (
          <figcaption className="text-xs text-muted-foreground mt-1 text-center">{img.caption}</figcaption>
        )}
      </figure>
    ))}
  </div>
);

// whitespace-pre keeps multi-line blocks (code listings extracted from docx
// sources) intact; single-line equations render exactly as before.
const Equation = ({ text }) => (
  <p className="font-mono text-sm md:text-base bg-muted/50 rounded-md px-4 py-3 my-4 overflow-x-auto whitespace-pre">
    {text}
  </p>
);

const LinkList = ({ note, links }) => (
  <div>
    {note && <p className="text-sm text-muted-foreground">{note}</p>}
    <ul className="space-y-2 list-disc pl-5 mt-2">
      {(links || []).map((link, i) => (
        <li key={i}>
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-autoxium-blue hover:underline"
          >
            {link.text || link.url}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

// Content fields (paragraph text, bullet items) may contain simple inline HTML (e.g.
// <b>...</b>) — same convention already used server-side for PDF body_content. This is
// admin-authored content behind the admin role check, not arbitrary user input.
const Html = ({ text, as: Tag = 'span' }) => <Tag dangerouslySetInnerHTML={{ __html: text }} />;

// Renders an ordered list of content blocks (paragraph / bullet_list / param_table /
// step_list / callout / figure / equation / link_list) shared by Articles and Tool/
// Simulator User Guides — the one place a block "type" maps to its visual component.
const BlockRenderer = ({ blocks }) => (
  <>
    {(blocks || []).map((block, i) => {
      switch (block.type) {
        case 'paragraph':
          return <Html key={i} as="p" text={block.text} />;
        case 'bullet_list':
          return (
            <BulletList
              key={i}
              items={(block.items || []).map((item, j) => <Html key={j} text={item} />)}
            />
          );
        case 'param_table':
          return <ParamTable key={i} columns={block.columns} rows={block.rows} />;
        case 'step_list':
          return <StepList key={i} steps={block.steps} />;
        case 'callout':
          return (
            <Callout key={i} title={block.title} tone={block.tone || 'info'}>
              <Html text={block.text} />
            </Callout>
          );
        case 'figure':
          return (
            <Figure
              key={i}
              src={resolveImageSrc(block.image_path)}
              alt={block.alt || block.caption || ''}
              caption={block.caption}
            />
          );
        case 'image_gallery':
          return <ImageGallery key={i} images={block.images} />;
        case 'equation':
          return <Equation key={i} text={block.text} />;
        case 'link_list':
          return <LinkList key={i} note={block.note} links={block.links} />;
        default:
          return null;
      }
    })}
  </>
);

export default BlockRenderer;
